import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { m, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import FormEmbed from "@/components/FormEmbed";

const QuoteFormContext = createContext<{ openQuoteForm: () => void }>({
  openQuoteForm: () => {},
});

export const useQuoteForm = () => useContext(QuoteFormContext);

export const QuoteFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openQuoteForm = useCallback(() => setOpen(true), []);
  const closeQuoteForm = useCallback(() => setOpen(false), []);

  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  /** Element that had focus before the dialog opened, so we can hand it back. */
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      restoreFocusRef.current?.focus();
      restoreFocusRef.current = null;
      return;
    }

    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeQuoteForm();
        return;
      }
      if (e.key !== "Tab") return;

      // Keep Tab inside the dialog. The form itself lives in a cross-origin
      // iframe, so the panel's own controls are all we can enumerate.
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), iframe, input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, closeQuoteForm]);

  return (
    <QuoteFormContext.Provider value={{ openQuoteForm }}>
      {children}
      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/65 backdrop-blur-sm p-3 sm:p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closeQuoteForm(); }}
          >
            <m.div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="quote-form-title"
              aria-describedby="quote-form-description"
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              /* Cap to the viewport and scroll inside, so the panel — and its close
                 button — can never be pushed off screen by the form's own height. */
              className="relative w-full max-w-lg widget-shell flex flex-col max-h-[calc(100dvh-1.5rem)] sm:max-h-[calc(100dvh-2rem)]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header stays pinned so Close is always reachable */}
              <div className="relative shrink-0 bg-card border-b border-border px-6 pt-5 pb-4 pr-16">
                <h2
                  id="quote-form-title"
                  className="font-heading text-xl sm:text-2xl text-foreground font-bold tracking-tight"
                >
                  Get Your Free Estimate
                </h2>
                <p id="quote-form-description" className="text-muted-foreground text-sm mt-1">
                  Tell us about your project or damage — we'll get right back to you.
                </p>
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={closeQuoteForm}
                  className="absolute top-4 right-4 h-9 w-9 rounded-full bg-secondary text-secondary-foreground shadow-md hover:bg-secondary/90 transition-colors flex items-center justify-center"
                  aria-label="Close form"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Embedded LeadConnector form — scrolls within the capped panel */}
              <div className="overflow-y-auto overscroll-contain">
                <FormEmbed placement="popup" className="p-4 sm:p-6" />
              </div>
            </m.div>
          </m.div>
        )}
      </AnimatePresence>
    </QuoteFormContext.Provider>
  );
};
